import type { Player, PlayerId, GameState, WarriorInstance } from '@/types';
import type { NetworkRole } from '@network/NetworkController';

/**
 * View-oriented player representation.
 * "local" always appears at bottom of screen, "remote" always at top.
 * This abstraction allows the rendering code to be agnostic of network role.
 */
export interface ViewPlayers {
  readonly local: Player;
  readonly remote: Player;
  readonly localId: PlayerId;
  readonly remoteId: PlayerId;
}

/**
 * View state for rendering - abstracts away network role perspective.
 * All rendering code should use this instead of raw GameState when
 * determining local vs remote positioning.
 */
export interface ViewState {
  readonly viewPlayers: ViewPlayers;
  readonly isLocalTurn: boolean;
  readonly selectedWarriorId: string | null;
  readonly selectedWarriorIsLocal: boolean;
  readonly localWarriors: readonly WarriorInstance[];
  readonly remoteWarriors: readonly WarriorInstance[];
  readonly allWarriors: readonly WarriorInstance[];
  readonly turnNumber: number;
}

/**
 * Create view state from game state.
 * When the local player is the guest (player2 in online mode),
 * the players are swapped so that local always appears at bottom.
 *
 * @param state - The current game state
 * @param networkRole - The network role (host/guest) or null for offline modes
 * @returns ViewState with players mapped to local/remote positions
 */
export function createViewState(
  state: GameState,
  networkRole: NetworkRole | null
): ViewState {
  const isGuest = state.gameMode === 'online' && networkRole === 'guest';

  // For guest: swap so player2 becomes "local" (bottom)
  // For everyone else: player1 is "local" (bottom)
  const viewPlayers: ViewPlayers = isGuest
    ? {
        local: state.players.player2,
        remote: state.players.player1,
        localId: 'player2',
        remoteId: 'player1',
      }
    : {
        local: state.players.player1,
        remote: state.players.player2,
        localId: 'player1',
        remoteId: 'player2',
      };

  const localWarriors = viewPlayers.local.warriors;
  const remoteWarriors = viewPlayers.remote.warriors;
  const allWarriors = [...localWarriors, ...remoteWarriors];

  const selectedWarriorId = state.selectedWarrior;
  const selectedWarrior = selectedWarriorId
    ? allWarriors.find((w) => w.instanceId === selectedWarriorId) ?? null
    : null;

  const selectedWarriorIsLocal = selectedWarrior
    ? selectedWarrior.ownerId === viewPlayers.localId
    : false;

  return {
    viewPlayers,
    isLocalTurn: state.currentTurn === viewPlayers.localId,
    selectedWarriorId,
    selectedWarriorIsLocal,
    localWarriors,
    remoteWarriors,
    allWarriors,
    turnNumber: state.turnNumber,
  };
}

/**
 * Check if a warrior belongs to the remote player (should appear at top, rotated).
 */
export function isRemoteWarrior(
  warrior: WarriorInstance,
  viewPlayers: ViewPlayers
): boolean {
  return warrior.ownerId === viewPlayers.remoteId;
}
